import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Tabs, 
  Tab, 
  Grid, 
  Card, 
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Snackbar,
  Skeleton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Notes as NotesIcon,
  NotificationImportant as AlertsIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { EncounterType } from '../../types/enums';
import { useProcedures } from '../Procedures/hooks/useProcedures';
import DischargeActivity from './DischargeActivity';
import PersonalizedReporting from '../reports/PersonalizedReporting';
import EncounterDocumentsTab from './EncounterDocumentsTab';
import CareTeam from '../CareTeam/CareTeam';
import AlertsTab from './AlertsTab';
import BedAssignmentManager from '../bed-assignment/BedAssignmentManager';
import { SurgicalProcedureIntegrationDemo } from '../Procedures';
import NotesHistoryTab from './NotesHistoryTab';
import OrdersTableWithTabs from './OrdersTableWithTabs';
import PrescriptionsTab from './PrescriptionsTab';
import ClinicalDataTab from './ClinicalDataTab';
import EncounterChargesTab from './EncounterChargesTab';
import { useUserData } from '../../hooks/useUserData';

// Lazy loading wrapper - only renders children when shouldLoad is true
const LazyTabContent = ({ shouldLoad, children, loadingHeight = 200 }) => {
  if (!shouldLoad) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minHeight: loadingHeight }}>
        <Skeleton variant="rectangular" width="100%" height={40} />
        <Skeleton variant="rectangular" width="100%" height={80} />
        <Typography variant="body2" color="text.secondary">
          Click tab to load content
        </Typography>
      </Box>
    );
  }
  return children;
};

// Loading fallback for tab content
const TabLoadingFallback = ({ height = 200 }) => (
  <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: height }}>
    <CircularProgress size={24} sx={{ mr: 1 }} />
    <Typography variant="body2" color="text.secondary">Loading...</Typography>
  </Box>
);



// Styled Components
const CompactSection = ({ children, ...props }) => (
  <Box
    sx={{
      backgroundColor: '#ffffff',
      border: '1px solid #e0e0e0',
      borderRadius: 1.5,
      mb: 0.5,
      '&:last-child': { mb: 0 },
    }}
    {...props}
  >
    {children}
  </Box>
);

const SectionHeader = ({ children, ...props }) => (
  <Typography
    variant="subtitle1"
    sx={{
      fontWeight: 600,
      fontSize: '1rem',
      color: 'primary.main',
      p: 1,
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: '#fafafa',
      m: 0,
    }}
    {...props}
  >
    {children}
  </Typography>
);

const SectionHeaderWithIcon = ({ icon: Icon, children, ...props }) => (
  <SectionHeader {...props}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {Icon && <Icon sx={{ fontSize: '1.2rem' }} />}
      {children}
    </Box>
  </SectionHeader>
);

const SectionContent = ({ children, ...props }) => (
  <Box sx={{ p: 1.5 }} {...props}>
    {children}
  </Box>
);

export const useEncounterTabs = ({ user,
  encounter,
  patient,
  prescriptions,
  formData,
  setFormData,
  mode,
  encounterType,
  facilityId,
  onCareTeamChange,
  onRefreshEncounter,
  renderTaskList,
  showExtraTabs,
  handleSendOrder,
  orders,
  loadingOrders,
  encounterData,
  onOpenAllergiesDialog,
  onOpenQuickListDialog,
  onOpenPrescriptionDialog,
  activeTabIndex = 0, // Add activeTabIndex to support lazy loading
}) => {
  const { defaultFacility } = useUserData();
  
  // Track which tabs have been visited for lazy loading
  const [visitedTabs, setVisitedTabs] = useState(new Set([0])); // Tab 0 (Clinical Data) is always visited
  
  // Update visited tabs when active tab changes
  useEffect(() => {
    if (activeTabIndex !== undefined && !visitedTabs.has(activeTabIndex)) {
      setVisitedTabs(prev => new Set([...prev, activeTabIndex]));
    }
  }, [activeTabIndex, visitedTabs]);
  
  // Only fetch procedures data when Orders tab (index 1) has been visited
  // This is the major optimization - defer loading until tab is clicked
  const shouldLoadOrders = visitedTabs.has(1);
  const patientIdForOrders = shouldLoadOrders ? (encounter?.patientId || patient?.id) : undefined;
  
  // Hook for orders data - only fetches when patientIdForOrders is defined
  const { procedures: ordersData, loading: loadingOrdersData } = useProcedures(patientIdForOrders);
  
  // Use provided orders or fallback to hook data - memoized to prevent new array reference on each render
  const finalOrders = useMemo(() => orders || ordersData || [], [orders, ordersData]);
  const finalLoadingOrders = shouldLoadOrders ? (loadingOrders || loadingOrdersData) : false;
  
  // Filter surgical orders
  const surgicalOrders = finalOrders?.filter(order => order.orderType === 'Surgical' || order.testType === 'Surgical') || [];
  const hasSurgicalOrders = surgicalOrders.length > 0;

  const isInpatient = encounterType === EncounterType.INPATIENT_ADMISSION || encounterType === 'inpatient';
  const isEmergencyVisit = encounterType === EncounterType.EMERGENCY_VISIT || encounterType === 'emergency';

  // Check if encounter is created - tabs other than Clinical Data are disabled until encounter is saved
  const isEncounterCreated = !!encounter?.id;
  
  // Helper to check if a tab has been visited (for lazy loading)
  const isTabVisited = useCallback((tabIndex) => visitedTabs.has(tabIndex), [visitedTabs]);

  // Define all tabs with lazy loading flags
  // Tabs with lazyLoad: true will only render content when the tab is visited
  const allTabDefs = [
    {
      label: 'Clinical Data',
      tabKey: 'clinical',
      show: true,
      disabled: false, // Always enabled - this is the entry point for creating encounters
      lazyLoad: false, // Always load immediately
      render: (
        <ClinicalDataTab
          encounter={encounter}
          patient={patient}
          formData={formData}
          setFormData={setFormData}
          mode={mode}
          encounterData={encounterData}
          onOpenAllergiesDialog={onOpenAllergiesDialog}
          onOpenQuickListDialog={onOpenQuickListDialog}
        />
      )
    },
    {
      label: (() => {
        const draftCount = finalOrders.filter(order => order.status === 'Saved as Draft').length;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Orders
            {draftCount > 0 && (
              <Chip 
                label={draftCount}
                size="small"
                color="warning"
                sx={{ 
                  fontSize: '0.7rem', 
                  height: '18px',
                  minWidth: '18px',
                  animation: 'flash 1.5s infinite',
                  '@keyframes flash': {
                    '0%, 50%': { opacity: 1 },
                    '25%, 75%': { opacity: 0.5 },
                  }
                }}
              />
            )}
          </Box>
        );
      })(),
      tabKey: 'orders',
      show: true,
      disabled: !isEncounterCreated,
      lazyLoad: true, // Lazy load - fetches procedures data on visit
      render: (
        <CompactSection>
          <SectionHeader>Orders & Procedures</SectionHeader>
          <SectionContent>
            <OrdersTableWithTabs 
              orders={finalOrders} 
              loading={finalLoadingOrders} 
              onSendOrder={handleSendOrder}
            />
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: 'Prescriptions',
      tabKey: 'prescriptions',
      show: true,
      disabled: !isEncounterCreated,
      lazyLoad: false, // Already loaded from aggregated endpoint
      render: (
        <CompactSection>
          <SectionHeader>Prescriptions</SectionHeader>
          <SectionContent>
            <PrescriptionsTab 
              prescriptions={prescriptions || []}
              patient={patient}
              encounter={encounter}
              onPrescriptionsChange={(updatedPrescriptions) => {
                console.log('Prescriptions updated:', updatedPrescriptions);
              }}
              onAddPrescription={onOpenPrescriptionDialog}
            />
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: hasSurgicalOrders ? `Surgery (${surgicalOrders.length})` : 'Surgery',
      tabKey: 'surgery',
      show: hasSurgicalOrders,
      disabled: !isEncounterCreated,
      lazyLoad: true,
      render: (
        <CompactSection>
          <SectionHeaderWithIcon icon={MedicalServicesIcon}>Surgical Procedures</SectionHeaderWithIcon>
          <SectionContent>
            <SurgicalProcedureIntegrationDemo
              encounterId={encounter?.id || ''}
              patientId={encounter?.patientId || patient?.id || ''}
              fin={encounter?.fin}
              showIntegrationExample={false}
            />
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: 'Task List',
      tabKey: 'tasks',
      show: isInpatient && renderTaskList,
      disabled: !isEncounterCreated,
      lazyLoad: true,
      render: (
        <CompactSection>
          <SectionHeader>Workflow Tasks</SectionHeader>
          <SectionContent>
            {renderTaskList && renderTaskList()}
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: 'Care Team',
      tabKey: 'careteam',
      show: isInpatient,
      disabled: !isEncounterCreated,
      lazyLoad: true, // Lazy load - fetches care team data on visit
      render: (
        <CompactSection>
          <SectionHeader>Care Team</SectionHeader>
          <SectionContent>
            <CareTeam
              patientId={encounter?.patientId || patient?.id}
              encounterId={encounter?.id}
              onCareTeamChange={onCareTeamChange}
            />
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: 'Notes History',
      tabKey: 'notes',
      show: true, // Show for all encounter types instead of just isInpatient
      disabled: !isEncounterCreated,
      lazyLoad: true, // Lazy load - fetches notes on visit
      render: (
        <CompactSection>
          <SectionHeaderWithIcon icon={NotesIcon}>Notes History</SectionHeaderWithIcon>
          <SectionContent>
            <NotesHistoryTab
              encounterId={encounter?.id}
              patient={patient}
            />
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: 'Alerts',
      tabKey: 'alerts',
      show: isInpatient,
      disabled: !isEncounterCreated,
      lazyLoad: true,
      render: (
        <CompactSection>
          <SectionHeaderWithIcon icon={AlertsIcon}>Patient Alerts</SectionHeaderWithIcon>
          <SectionContent>
            <AlertsTab 
              encounter={encounter}
              patient={patient}
            />
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: 'Room/Bed Assignment',
      tabKey: 'bed',
      show: isInpatient || encounterType === EncounterType.OBSERVATION || encounterType === 'observation' || isEmergencyVisit || encounterType === EncounterType.OUTPATIENT_VISIT || encounterType === 'outpatient',
      disabled: !isEncounterCreated,
      lazyLoad: true,
      render: (
        <CompactSection>
          <SectionHeader>Bed Assignment Management</SectionHeader>
          <SectionContent>
            {encounter?.id ? (
              <BedAssignmentManager
                facilityId={defaultFacility?.id || ''}
                patientId={encounter?.patientId || patient?.id}
                encounterId={encounter?.id}
                patient={patient}
                encounter={encounter}
                onRefreshEncounter={onRefreshEncounter}
              />
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2">
                  Loading encounter data...
                </Typography>
              </Box>
            )}
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: 'Reports',
      tabKey: 'reports',
      show: showExtraTabs,
      disabled: !isEncounterCreated,
      lazyLoad: true,
      render: (
        <CompactSection>
          <SectionHeader>Reports</SectionHeader>
          <SectionContent>
            <PersonalizedReporting 
              facilityId={facilityId}
              patientId={encounter?.patientId || patient?.id}
              encounterId={encounter?.id}
              patient={patient}
              encounter={encounter}
              showPatientHeader={false}
            />
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: 'Documents',
      tabKey: 'documents',
      show: showExtraTabs,
      disabled: !isEncounterCreated,
      lazyLoad: true,
      render: (
        <CompactSection>
          <SectionHeader>Documents</SectionHeader>
          <SectionContent>
            <EncounterDocumentsTab
              encounter={encounter}
              patient={patient}
              onDocumentsChange={(documents) => {
                console.log('Documents updated:', documents);
              }}
            />
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: 'Charges',
      tabKey: 'charges',
      show: true, // Always show charges tab for billing review
      disabled: !isEncounterCreated,
      lazyLoad: true,
      render: (
        <CompactSection>
          <SectionHeaderWithIcon icon={MoneyIcon}>Encounter Charges</SectionHeaderWithIcon>
          <SectionContent>
            <EncounterChargesTab
              encounter={encounter}
              patient={patient}
              orders={finalOrders}
              onChargesChange={(charges) => {
                console.log('Encounter charges updated:', charges);
                // Store charges in formData for use during discharge/billing
                if (setFormData) {
                  setFormData(prev => ({
                    ...prev,
                    charges: charges
                  }));
                }
              }}
            />
          </SectionContent>
        </CompactSection>
      )
    },
    {
      label: 'Discharge',
      tabKey: 'discharge',
      show: true, // Always show discharge tab for all encounters
      disabled: !isEncounterCreated,
      lazyLoad: false, // Don't lazy load - important for workflow
      render: (
        <CompactSection>
          <SectionHeader>Discharge Planning</SectionHeader>
          <SectionContent>
            <DischargeActivity
              encounter={encounter}
              patient={patient}
              formData={formData}
              setFormData={setFormData}
            />
          </SectionContent>
        </CompactSection>
      )
    },
  ];

  // Filter visible tabs and wrap lazy-loaded tabs with LazyTabContent
  const tabDefs = allTabDefs
    .filter(tab => tab.show)
    .map((tab, index) => ({
      ...tab,
      render: tab.lazyLoad ? (
        <LazyTabContent shouldLoad={isTabVisited(index)} loadingHeight={250}>
          {tab.render}
        </LazyTabContent>
      ) : tab.render
    }));

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Check if default facility is missing
  useEffect(() => {
    if (!defaultFacility?.id) {
      setSnackbar({
        open: true,
        message: 'Default facility is not set. Please set your default facility to manage bed assignments.',
        severity: 'error'
      });
    } else {
      // Clear the snackbar if it was showing the default facility error
      setSnackbar(prev => {
        if (prev.message === 'Default facility is not set. Please set your default facility to manage bed assignments.') {
          return { ...prev, open: false };
        }
        return prev;
      });
    }
  }, [defaultFacility?.id]);

  // Method to mark a tab as visited (call this when switching tabs)
  const markTabVisited = useCallback((tabIndex) => {
    setVisitedTabs(prev => {
      if (prev.has(tabIndex)) return prev;
      return new Set([...prev, tabIndex]);
    });
  }, []);

  return { 
    tabDefs,
    snackbar,
    setSnackbar,
    visitedTabs,
    markTabVisited,
    snackbarComponent: (
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    )
  };
}; 